<template>
  <div class="container-fluid px-4 mt-4">
    <!-- Bütün sayfayı kaplayan loading ekranı -->
    <div v-if="loading" class="loading-overlay">
      <b-spinner variant="primary" label="Loading..."></b-spinner>
      <p>Yükleniyor...</p>
    </div>

    <h4 class="mb-4 font-weight-bold text-dark text-center">Ödeme Listesi</h4>

    <b-row class="justify-content-center">
      <b-col cols="12" md="6" lg="4">
        <b-form @submit.prevent="downloadReport" class="p-3 bg-white border rounded">
          <b-form-group label="Başlangıç Tarihi" label-for="start-date">
            <b-form-datepicker
                id="start-date"
                v-model="reportForm.from"
                required
                placeholder="Başlangıç Tarihi seçiniz"
            />
          </b-form-group>

          <b-form-group label="Bitiş Tarihi" label-for="end-date">
            <b-form-datepicker
                id="end-date"
                v-model="reportForm.to"
                required
                placeholder="Bitiş Tarihi seçiniz"
            />
          </b-form-group>

          <div class="d-flex justify-content-end">
            <b-button
                variant="primary"
                type="submit"
                class="ml-2"
                :disabled="!reportForm.from || !reportForm.to"
            >
              İndir
            </b-button>
          </div>
        </b-form>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import PaymentService from "@/service/PaymentService";
import * as XLSX from "xlsx"; // npm i xlsx

export default {
  name: 'admin-payment-report',
  data() {
    return {
      loading: false,
      reportForm: { from: null, to: null },
    };
  },
  methods: {
    async downloadReport() {
      this.loading = true;
      try {
        const res = await PaymentService.getPaymentReport(this.reportForm);
        const report = res?.data ?? res; // axios ise res.data, değilse doğrudan res
        this.exportPaymentsExcel(report);
      } finally {
        this.loading = false;
      }
    },

    exportPaymentsExcel(report) {
      const clients = report?.clientPayments ?? [];
      if (!clients.length) {
        this.$bvToast?.toast("Kayıt bulunamadı.", { variant: "warning" });
        return;
      }

      // Her client için en çok kaç ödeme var?
      const maxPayments = Math.max(0, ...clients.map(c => (c.payments?.length || 0)));

      // ---- 2D dizi (AOA) oluşturma ----
      const aoa = [];

      // 1) Üst başlık (clientName) - her client 2 kolon kaplar ve merge edilecek
      const rowClientNames = [];
      clients.forEach(c => {
        rowClientNames.push(c.clientName || "", ""); // ikinci hücre merge sebebiyle boş
      });
      aoa.push(rowClientNames);

      // 2) Alt başlık: Tarih / Tutar
      const rowSubHeaders = [];
      //clients.forEach(() => rowSubHeaders.push("Tarih", "Tutar"));
      aoa.push(rowSubHeaders);

      // 3) Ödeme satırları
      for (let i = 0; i < maxPayments; i++) {
        const row = [];
        clients.forEach(c => {
          const item = c.payments?.[i];
          row.push(item?.date ?? "", item?.amount ?? "");
        });
        aoa.push(row);
      }

      // 4) Müşteri toplamları (aynı satırda, her client kendi kolonunda)
      const totalsRow = [];
      clients.forEach(c => {
        totalsRow.push("Toplam", c.clientTotalAmount ?? 0);
      });
      aoa.push(totalsRow);

      // 5) Boş satır
      aoa.push([]);

      // 6) Genel Toplam (1. kolonda 2 satır aşağı)
      const grandTotalRowIdx = 2 + maxPayments + 1 /*totals*/ + 2 /*boş+1*/;
      while (aoa.length <= grandTotalRowIdx) aoa.push([]);
      aoa[grandTotalRowIdx][0] = "Genel Toplam";
      aoa[grandTotalRowIdx][1] = report?.totalAmount ?? 0;

      // ---- Sheet oluşturma ----
      const ws = XLSX.utils.aoa_to_sheet(aoa);

      // Client name hücrelerini 2 kolona merge et
      ws["!merges"] = ws["!merges"] || [];
      clients.forEach((_, idx) => {
        const startCol = idx * 2; // 0-based
        ws["!merges"].push({
          s: { r: 0, c: startCol },     // row 0, col start
          e: { r: 0, c: startCol + 1 }, // row 0, col end (2 kolon)
        });
      });

      // Kolon genişlikleri (isteğe bağlı)
      ws["!cols"] = [];
      clients.forEach(() => {
        ws["!cols"].push({ wch: 12 }, { wch: 14 }); // Tarih ~12, Tutar ~14
      });

      // Tutar hücrelerini sayı formatına çevir (#,##0.0000)
      const encode = XLSX.utils.encode_cell;
      const amountCols = clients.map((_, idx) => idx * 2 + 1); // B,D,F,... (0-based)
      const firstAmountRow = 2; // ödeme satırları başlangıcı (0-based, 3. satır)
      //const lastAmountRow = 2 + maxPayments; // ödemelerin bittiği satır
      const totalsRowIdx = 2 + maxPayments;  // toplamların olduğu satır indexi (0-based)
      const grandTotalRow = grandTotalRowIdx; // zaten 0-based olarak kurduk

      const setNumeric = (r, c) => {
        const addr = encode({ r, c });
        const cell = ws[addr];
        if (!cell) return;
        let v = cell.v;
        if (typeof v === "string" && v.trim() !== "") {
          const num = Number(v.toString().replace(/\s/g, "").replace(",", ".")); // olası virgül düzeltmesi
          if (!Number.isNaN(num)) v = num;
        }
        if (typeof v === "number") {
          cell.v = v;
          cell.t = "n";
          cell.z = "#,##0.0000";
        }
      };

      // Ödeme satırları + müşteri toplam hücreleri
      for (let r = firstAmountRow; r <= totalsRowIdx; r++) {
        amountCols.forEach(c => setNumeric(r, c));
      }
      // Genel Toplam (B sütunu)
      setNumeric(grandTotalRow, 1);

      // ---- Workbook ve indirme ----
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Ödeme Listesi");

      const from = this.reportForm.from || "";
      const to = this.reportForm.to || "";
      const fileName = `odeme_listesi_${from}_${to}.xlsx`;
      XLSX.writeFile(wb, fileName);
    },
  },
};
</script>


<style scoped>

</style>
